import { Button, IconButton, TextLink, Typography, Wizard } from '@neo4j-ndl/react';
import {
  CloudArrowUpIconOutline,
  CodeBracketIconOutline,
  CommandLineIconOutline,
  CubeTransparentIconOutline,
  LightBulbIconOutline,
  LinkIconOutline,
  RocketLaunchIconOutline,
  SparklesIconOutline,
  StatusIcon,
  UserPlusIconOutline,
  WrenchIconOutline,
} from '@neo4j-ndl/react/icons';
import { PLG_EVENTS } from '@nx/analytics-service';
import { APP_SCOPE } from '@nx/constants';
import {
  ACTION,
  MODAL_TYPE,
  useActiveOrg,
  useActiveProject,
  useAuraUser,
  useModal,
  usePermissions,
  useToolsConfiguration,
  useTrackEvent,
} from '@nx/state';
import { ConnectionBar, Page, PermissionTooltip } from '@nx/ui';
import { useNavigate } from 'react-router';

import { Card } from './card';
import { Section } from './section';

export const TrialHomePage = () => {
  const activeProject = useActiveProject();
  const { user } = useAuraUser();
  const projectInviteModal = useModal(MODAL_TYPE.PROJECT_INVITE);
  const toolsConfiguration = useToolsConfiguration();
  const navigate = useNavigate();
  const { isAllowed } = usePermissions();
  const track = useTrackEvent();
  const allowInvite = isAllowed(ACTION.CREATE, `namespaces/${activeProject.id}/invites`);
  const activeOrg = useActiveOrg();
  const sampleCalloutVisible = activeOrg.featureToggles['plg-sample-applications'];

  const handleInviteUsersClick = () => {
    projectInviteModal.open({ project: activeProject });
    track({
      event: PLG_EVENTS.GET_STARTED_BUTTON,
      scope: APP_SCOPE.aura,
      properties: {
        button: 'invite-user',
        org_id: activeProject.organizationId,
        user_id: user?.id,
      },
    });
  };

  return (
    <div className="h-full w-full overflow-auto">
      <ConnectionBar />
      <Page contentType="extra-light" className="@container flex flex-col gap-[52px]">
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <Typography variant="h1">Get started</Typography>
            <div className="flex flex-wrap items-center justify-end gap-2">
              <PermissionTooltip hasPermission={allowInvite} hasButtonWrapper className="flex items-center">
                <IconButton
                  className="@sm:hidden"
                  ariaLabel="Invite your team"
                  onClick={handleInviteUsersClick}
                  isDisabled={!allowInvite}
                >
                  <UserPlusIconOutline />
                </IconButton>
                <Button
                  color="neutral"
                  fill="outlined"
                  className="@sm:flex hidden gap-2"
                  size="small"
                  onClick={handleInviteUsersClick}
                  isDisabled={!allowInvite}
                >
                  <UserPlusIconOutline className="size-4 shrink-0" />
                  Invite your team
                </Button>
              </PermissionTooltip>
              <IconButton
                className="@sm:hidden"
                ariaLabel="Ask Neo4j AI"
                onClick={() => {
                  track({
                    event: PLG_EVENTS.GET_STARTED_LINK,
                    scope: APP_SCOPE.aura,
                    properties: {
                      link: 'docs',
                      org_id: activeProject.organizationId,
                      user_id: user?.id,
                    },
                  });
                }}
                htmlAttributes={{ href: 'https://neo4j.com/docs/?chatbot=open', target: '_blank', rel: 'noreferrer' }}
                as="a"
              >
                <SparklesIconOutline />
              </IconButton>
              <Button
                color="neutral"
                fill="outlined"
                className="@sm:flex hidden gap-2"
                size="small"
                onClick={() => {
                  track({
                    event: PLG_EVENTS.GET_STARTED_LINK,
                    scope: APP_SCOPE.aura,
                    properties: {
                      link: 'docs',
                      org_id: activeProject.organizationId,
                      user_id: user?.id,
                    },
                  });
                }}
                htmlAttributes={{ href: 'https://neo4j.com/docs/?chatbot=open', target: '_blank', rel: 'noreferrer' }}
                as="a"
              >
                <SparklesIconOutline className="size-4 shrink-0" />
                Ask Neo4j AI
              </Button>
            </div>
          </div>
          <Typography variant="body-medium" className="text-neutral-text-weak">
            Explore the content below to maximize your Neo4j Aura experience
          </Typography>
          {sampleCalloutVisible && (
            <div className="border-neutral-border-weak bg-neutral-bg-weak flex flex-row items-center justify-between gap-4 rounded-lg border p-4">
              <div className="bg-primary-bg-weak size-8 rounded-full p-2">
                <LightBulbIconOutline className="size-4" />
              </div>
              <div className="flex flex-1 flex-col">
                <Typography variant="subheading-small">Get inspired with what Neo4j can do</Typography>
                <Typography variant="body-small" className="text-neutral-text-weak">
                  Try our ready-to-use demos to get inspired
                </Typography>
              </div>
              <Button
                onClick={() => {
                  // TODO: add a link when available
                }}
                size="small"
                color="neutral"
                fill="outlined"
              >
                Explore demos
              </Button>
            </div>
          )}
        </div>
        <div className="g">
          <Wizard
            className="custom-wizard relative !grid-rows-none gap-x-8"
            activeStepIndex={3}
            steps={[
              {
                content: (
                  <Section
                    title={
                      <>
                        <CloudArrowUpIconOutline className="size-6" />
                        <Typography variant="h5">Import your data</Typography>
                      </>
                    }
                    description="Data in Neo4j is stored as a graph rather than tables. Import your data to begin organizing it into nodes and relationships."
                    buttonOptions={{
                      text: 'Import your data',
                      action: () => {
                        void navigate(toolsConfiguration.import?.route ?? '/tools/import');
                        track({
                          event: PLG_EVENTS.GET_STARTED_LINK,
                          scope: APP_SCOPE.aura,
                          properties: {
                            link: 'import',
                            org_id: activeProject.organizationId,
                            user_id: user?.id,
                          },
                        });
                      },
                      animated: true,
                    }}
                    highlighted
                  />
                ),
              },
              {
                content: (
                  <Section
                    title={
                      <>
                        <CommandLineIconOutline className="size-6" />
                        <Typography variant="h5">Query your data</Typography>
                      </>
                    }
                    description="Unlock insights in your graph with Cypher, Neo4j's powerful query language. Effortlessly explore connections, analyze patterns, and gain deeper understanding of your connected data."
                    buttonOptions={{
                      text: 'Go to Query',
                      action: () => {
                        void navigate(toolsConfiguration.query?.route ?? '/tools/query');
                        track({
                          event: PLG_EVENTS.GET_STARTED_LINK,
                          scope: APP_SCOPE.aura,
                          properties: {
                            link: 'query',
                            org_id: activeProject.organizationId,
                            user_id: user?.id,
                          },
                        });
                      },
                    }}
                    highlighted
                    link={{
                      text: 'Cypher manual',
                      href: 'https://neo4j.com/docs/getting-started/cypher/',
                      external: true,
                      onClick: () => {
                        track({
                          event: PLG_EVENTS.GET_STARTED_LINK,
                          scope: APP_SCOPE.aura,
                          properties: {
                            link: 'cypher-docs',
                            org_id: activeProject.organizationId,
                            user_id: user?.id,
                          },
                        });
                      },
                    }}
                  />
                ),
              },
              {
                content: (
                  <Section
                    title={
                      <>
                        <CodeBracketIconOutline className="size-6" />
                        <Typography variant="h5">Build an application</Typography>
                      </>
                    }
                    description="Bring your ideas to life with Neo4j. Easily build an application or proof-of-concept with popular drivers, like Python and GraphQL, to unlock the power of your connected data."
                    buttonOptions={{
                      text: 'Go to Developer hub',
                      action: () => {
                        void navigate(`/projects/${activeProject.id}/developer-hub`);
                        track({
                          event: PLG_EVENTS.GET_STARTED_LINK,
                          scope: APP_SCOPE.aura,
                          properties: {
                            link: 'developer-hub',
                            org_id: activeProject.organizationId,
                            user_id: user?.id,
                          },
                        });
                      },
                    }}
                    link={{
                      text: 'Explore courses in GraphAcademy',
                      href: 'https://graphacademy.neo4j.com/categories/',
                      external: true,
                      onClick: () => {
                        track({
                          event: PLG_EVENTS.GET_STARTED_LINK,
                          scope: APP_SCOPE.aura,
                          properties: {
                            link: 'graph-academy',
                            org_id: activeProject.organizationId,
                            user_id: user?.id,
                          },
                        });
                      },
                    }}
                    highlighted
                  />
                ),
              },
              {
                content: (
                  <Section
                    title={
                      <>
                        <RocketLaunchIconOutline className="size-6" />
                        <Typography variant="h5">Neo4j in production</Typography>
                      </>
                    }
                    description="Prepare your Neo4j database for production. Optimize queries, enhance security, scale seamlessly, and more."
                    highlighted
                    extra={
                      <div className="@sm:grid-cols-2 mt-1 grid auto-rows-[1fr] gap-4">
                        <Card
                          title="Optimize your queries"
                          description="Fine-tune query performance with execution plans and tuning strategies."
                          icon={<WrenchIconOutline className="size-5" />}
                          actions={
                            <TextLink
                              href="https://neo4j.com/docs/cypher-manual/current/planning-and-tuning/"
                              type="external"
                              htmlAttributes={{
                                onClick: () => {
                                  track({
                                    event: PLG_EVENTS.GET_STARTED_LINK,
                                    scope: APP_SCOPE.aura,
                                    properties: {
                                      link: 'query-tuning-docs',
                                      org_id: activeProject.organizationId,
                                      user_id: user?.id,
                                    },
                                  });
                                },
                              }}
                            >
                              Query tuning
                            </TextLink>
                          }
                        />
                        <Card
                          title="Monitor your instance"
                          description="Track system health indicators to identify potential issues."
                          icon={<StatusIcon className="size-5" />}
                          actions={
                            <>
                              <Button
                                size="small"
                                color="neutral"
                                fill="outlined"
                                onClick={() => {
                                  void navigate(`/projects/${activeProject.id}/metrics`);
                                  track({
                                    event: PLG_EVENTS.GET_STARTED_LINK,
                                    scope: APP_SCOPE.aura,
                                    properties: {
                                      link: 'metrics',
                                      org_id: activeProject.organizationId,
                                      user_id: user?.id,
                                    },
                                  });
                                }}
                              >
                                Metrics
                              </Button>
                              <Button
                                size="small"
                                color="neutral"
                                fill="outlined"
                                onClick={() => {
                                  void navigate(`/projects/${activeProject.id}/logs`);
                                  track({
                                    event: PLG_EVENTS.GET_STARTED_LINK,
                                    scope: APP_SCOPE.aura,
                                    properties: {
                                      link: 'logs',
                                      org_id: activeProject.organizationId,
                                      user_id: user?.id,
                                    },
                                  });
                                }}
                              >
                                Logs
                              </Button>
                              <TextLink
                                href="https://neo4j.com/docs/aura/classic/platform/metrics-integration/"
                                type="external"
                                htmlAttributes={{
                                  onClick: () => {
                                    track({
                                      event: PLG_EVENTS.GET_STARTED_LINK,
                                      scope: APP_SCOPE.aura,
                                      properties: {
                                        link: 'metrics-docs',
                                        org_id: activeProject.organizationId,
                                        user_id: user?.id,
                                      },
                                    });
                                  },
                                }}
                              >
                                Docs
                              </TextLink>
                            </>
                          }
                        />
                        <Card
                          title="Automate Aura provisioning"
                          description="Easily create and manage instances using the Aura API and CLI."
                          icon={<CubeTransparentIconOutline className="size-5" />}
                          actions={
                            <>
                              <TextLink
                                href="https://neo4j.com/docs/aura/classic/platform/api/overview/"
                                type="external"
                                htmlAttributes={{
                                  onClick: () => {
                                    track({
                                      event: PLG_EVENTS.GET_STARTED_LINK,
                                      scope: APP_SCOPE.aura,
                                      properties: {
                                        link: 'aura-api',
                                        org_id: activeProject.organizationId,
                                        user_id: user?.id,
                                      },
                                    });
                                  },
                                }}
                              >
                                Aura API
                              </TextLink>
                              <TextLink
                                href="https://github.com/neo4j/aura-cli/blob/main/docs/usageGuide/A%20Guide%20To%20The%20New%20Aura%20CLI.md"
                                type="external"
                                htmlAttributes={{
                                  onClick: () => {
                                    track({
                                      event: PLG_EVENTS.GET_STARTED_LINK,
                                      scope: APP_SCOPE.aura,
                                      properties: {
                                        link: 'aura-cli',
                                        org_id: activeProject.organizationId,
                                        user_id: user?.id,
                                      },
                                    });
                                  },
                                }}
                              >
                                Aura CLI
                              </TextLink>
                            </>
                          }
                        />
                        <Card
                          title="Data integration"
                          description="Integrate your graph database with data sources, analytics tools, and applications."
                          icon={<LinkIconOutline className="size-5" />}
                          actions={
                            <TextLink
                              href="https://neo4j.com/docs/connectors/"
                              type="external"
                              htmlAttributes={{
                                onClick: () => {
                                  track({
                                    event: PLG_EVENTS.GET_STARTED_LINK,
                                    scope: APP_SCOPE.aura,
                                    properties: {
                                      link: 'connectors-docs',
                                      org_id: activeProject.organizationId,
                                      user_id: user?.id,
                                    },
                                  });
                                },
                              }}
                            >
                              Connectors
                            </TextLink>
                          }
                        />
                      </div>
                    }
                  />
                ),
              },
            ]}
            orientation="vertical"
            alignment="top"
          />
        </div>
      </Page>
    </div>
  );
};
